/**
 * Property Manager Passport — portfolio dashboard (Phase 2L-A107 MVP).
 *
 * Mirrors the customer `/passport` dashboard layout but scopes the row
 * set to properties where `managed_by_partner_id == current PM partner`.
 * Identical card markup so a PM and a customer recognise the same UI
 * vocabulary (Water Health Score™, last tested, next retest).
 *
 * Phase 1 scope: portfolio list + per-row link to the existing
 * `/passport/property/:id` page (whose backend was extended to allow
 * PM read access in routers/property_manager.is_property_manager_for).
 *
 * Out of scope (Phase 2): avg portfolio score, overdue-count tile,
 * bulk rebook, tenant context, conveyancer share workflow.
 */
import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2, MapPin, ArrowRight, LogOut, Calendar, ShieldCheck,
  AlertTriangle, FileText, Plus, TestTube2, AlertOctagon, Clock, FileQuestion, Filter as FilterIcon,
  Download, Users, Receipt,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { BrandLogo } from "@/components/BrandLogo";
import { HealthScoreGauge } from "@/components/passport/HealthScoreGauge";
import { PmAddPropertyModal } from "@/components/PmAddPropertyModal";
import { useFeature } from "@/lib/useFeature";
import { PmTabsBar, usePmTab } from "@/components/pm/PmTabsBar";
import PmOverviewTab from "@/components/pm/PmOverviewTab";
import PmSettingsTab from "@/components/pm/PmSettingsTab";
import PmLocalPartnerTab from "@/components/pm/PmLocalPartnerTab";
import PmPlaceholderTab from "@/components/pm/PmPlaceholderTab";
import PmBookTab from "@/components/pm/PmBookTab";
import PmLandlordsTab from "@/components/pm/PmLandlordsTab";
import PmCorrectiveLeadsTab from "@/components/pm/PmCorrectiveLeadsTab";
import PmWaterHealthTab from "@/components/pm/PmWaterHealthTab";

export default function PropertyManagerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isVisible: v2On } = useFeature("property_manager_v2");
  const [tab, setTab] = usePmTab("overview");
  const [partner, setPartner] = useState(null);
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  // Phase 2L-A132B — sessionStorage flag set on every PM-dashboard
  // mount, so child pages (Passport, Booking) can show a
  // "← Back to Managed Properties" button that returns the PM to
  // their portfolio context instead of the public homepage.
  useEffect(() => {
    try {
      sessionStorage.setItem("pm-context", "true");
    } catch { /* ignore quota / private-mode errors */ }
  }, []);

  /**
   * Phase 2L-A132C — invoice PDF download helper.
   * Hits the existing PM-only `/api/bookings/{ref}/invoice.pdf` endpoint
   * (login-gated, partner-scoped) using the authed axios instance, then
   * triggers a same-page blob download. The endpoint itself enforces
   * cross-agency isolation — a PM cannot download another agency's
   * invoice even by guessing a reference.
   */
  const downloadInvoice = async (reference) => {
    if (!reference) return;
    try {
      const res = await api.get(`/bookings/${reference}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SCWT-Invoice-${reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not download invoice. Please contact Aware support.");
    }
  };
  // Phase 2L-A119 — Optional client-side filter set by clicking a KPI
  // card. `null` = show all. Pure UI filter; the API result is the
  // single source of truth.
  const [kpiFilter, setKpiFilter] = useState(null); // null|'action'|'due_soon'|'overdue'|'awaiting'

  // Phase 2L-A119 — KPI computations. All values are derived from the
  // existing `/api/property-manager/properties` payload — NO extra
  // queries, no schema changes. `band === "action"` is the existing
  // Water Health Score severity bucket used elsewhere in the app
  // (passport_service.py / report_pdf_service.py). `next_due_at` is
  // an ISO string on `next_retest`.
  //
  // Phase 2L-A132 (Feb-2026 P0 fix) — `awaitingFirstTest` now also
  // checks `has_pending_booking`. The moment a PM invoice booking
  // is created the card flips out of "Awaiting first test" into a
  // "Booked · awaiting sample" state. Without this the PM saw the
  // exact same red "First test not booked" pill on a property they
  // had just successfully booked.
  const now = new Date();
  const in30 = new Date(now.getTime() + 30 * 24 * 3600 * 1000);
  const itemMeta = useMemo(
    () => items.map((it) => {
      const due = it.next_retest?.next_due_at ? new Date(it.next_retest.next_due_at) : null;
      const overdue = !!(due && due < now);
      const dueSoon = !!(due && !overdue && due <= in30);
      const actionRequired = (it.latest_test?.band || "") === "action";
      const hasPendingBooking = !!it.has_pending_booking;
      const awaitingFirstTest = !it.latest_test && !hasPendingBooking;
      return { it, overdue, dueSoon, actionRequired, awaitingFirstTest, hasPendingBooking };
    }),
    [items, now, in30],
  );
  const kpis = useMemo(() => ({
    total:    itemMeta.length,
    action:   itemMeta.filter((m) => m.actionRequired).length,
    dueSoon:  itemMeta.filter((m) => m.dueSoon).length,
    overdue:  itemMeta.filter((m) => m.overdue).length,
    awaiting: itemMeta.filter((m) => m.awaitingFirstTest).length,
  }), [itemMeta]);
  const filteredItems = useMemo(() => {
    if (!kpiFilter) return items;
    return itemMeta
      .filter((m) =>
        (kpiFilter === "action"   && m.actionRequired) ||
        (kpiFilter === "due_soon" && m.dueSoon) ||
        (kpiFilter === "overdue"  && m.overdue) ||
        (kpiFilter === "awaiting" && m.awaitingFirstTest))
      .map((m) => m.it);
  }, [items, itemMeta, kpiFilter]);
  const toggleKpiFilter = (key) =>
    setKpiFilter((current) => (current === key ? null : key));

  const loadPortfolio = () => {
    return api
      .get("/property-manager/properties")
      .then(({ data }) => {
        setPartner(data.partner);
        setItems(data.items || []);
        setActions(data.actions || []);
      })
      .catch((e) => setError(e.response?.data?.detail || "Could not load portfolio"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPortfolio(); }, []);
  // Phase 2L-A118 — fetch the agency logo as a blob (auth header is
  // attached automatically by the api client). Falls back silently
  // to the text-only chip when no logo exists / on any error.
  useEffect(() => {
    let cancelled = false;
    let objUrl = null;
    (async () => {
      try {
        const me = await api.get("/property-manager/me");
        if (!me?.data?.has_logo) return;
        const r = await api.get("/property-manager/logo", { responseType: "blob" });
        if (cancelled) return;
        objUrl = URL.createObjectURL(r.data);
        setLogoUrl(objUrl);
      } catch {
        // 403 / 404 / network — keep text-only fallback, no UI surfacing
      }
    })();
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
          <BrandLogo to="/" size="sm" />
          <div className="flex items-center gap-3">
            {user?.partner_id && (
              <>
                <Link
                  to="/property-manager/invoices"
                  data-testid="pm-dashboard-invoices-link"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-cyan-700 border border-slate-200 hover:border-cyan-300 rounded-full px-3 py-1.5 transition-colors"
                >
                  <Receipt className="w-3.5 h-3.5" /> Invoices
                </Link>
                <Link
                  to="/property-manager/team"
                  data-testid="pm-dashboard-team-link"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-cyan-700 border border-slate-200 hover:border-cyan-300 rounded-full px-3 py-1.5 transition-colors"
                >
                  <Users className="w-3.5 h-3.5" /> Team
                </Link>
              </>
            )}
            <span className="text-xs text-slate-500 hidden sm:inline" data-testid="pm-partner-business-name">
              {partner?.business_name || partner?.email || "Property Manager"}
            </span>
            <button
              onClick={async () => { await logout(); window.location.assign("/"); }}
              data-testid="pm-logout-btn"
              className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Branded portal intro — clarifies whose portal this is */}
        <section
          data-testid="pm-portal-header"
          className="relative overflow-hidden rounded-3xl border border-cyan-100 bg-gradient-to-br from-cyan-50 via-white to-slate-50 p-6 sm:p-8 lg:p-10"
        >
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-cyan-100/40 blur-3xl pointer-events-none" aria-hidden />
          <div className="relative">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">
              <ShieldCheck className="w-3.5 h-3.5" /> Property Manager Portal
            </div>
            <h1
              data-testid="pm-portal-h1"
              className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight"
            >
              <span data-testid="pm-portal-agency-name">
                {partner?.business_name || "Property Manager Portal"}
              </span>
              {partner?.business_name && (
                <span className="block text-cyan-700 text-lg sm:text-xl lg:text-2xl font-semibold mt-1.5">
                  Property Manager Portal
                </span>
              )}
            </h1>
            <p
              data-testid="pm-portal-subheading"
              className="mt-3 text-slate-600 max-w-2xl text-sm sm:text-base leading-relaxed"
            >
              Manage AWARE Water Health Records, bookings and invoices for your assigned properties.
            </p>

            {logoUrl && (
              <div
                data-testid="pm-agency-logo-wrap"
                className="mt-5 inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1.5 pr-4 py-1.5 text-xs sm:text-sm shadow-sm"
              >
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white border border-slate-100 overflow-hidden"
                >
                  <img
                    src={logoUrl}
                    alt={(partner?.business_name || "agency") + " logo"}
                    data-testid="pm-agency-logo"
                    className="max-w-full max-h-full object-contain"
                    onError={() => setLogoUrl(null)}
                  />
                </span>
                <span className="text-slate-500">Agency:</span>
                <span className="font-semibold text-slate-900 truncate max-w-[16rem] sm:max-w-none">
                  {partner?.business_name || partner?.email}
                </span>
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-2 text-[11px] sm:text-xs">
              <span
                data-testid="pm-portal-feature-passport"
                className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1 text-slate-700"
              >
                <ShieldCheck className="w-3 h-3 text-cyan-700" /> AWARE Water Health Records
              </span>
              <span
                data-testid="pm-portal-feature-retest"
                className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1 text-slate-700"
              >
                <Calendar className="w-3 h-3 text-cyan-700" /> Annual retest tracking
              </span>
              <span
                data-testid="pm-portal-feature-scope"
                className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1 text-slate-700"
              >
                <Building2 className="w-3 h-3 text-cyan-700" /> Assigned properties only
              </span>
            </div>
          </div>
        </section>

        {/* SCWT-PM-V2 Phase 3 — tabbed layout (only when flag ON).
             The legacy portfolio-only view renders when the flag is OFF. */}
        {v2On && <PmTabsBar active={tab} onChange={setTab} />}

        {v2On && tab === "overview"      && <PmOverviewTab />}
        {v2On && tab === "landlords"     && <PmLandlordsTab />}
        {v2On && tab === "book"          && <PmBookTab />}
        {v2On && tab === "tests"         && <PmPlaceholderTab testid="pm-tab-body-tests"       title="Tests"            subtitle="Track every sample from collection through to a published report." comingIn="Phase 4" />}
        {v2On && tab === "retests"       && <PmPlaceholderTab testid="pm-tab-body-retests"     title="Retests"          subtitle="See upcoming retest windows across your portfolio."  comingIn="Phase 5" />}
        {v2On && tab === "local_partner" && <PmLocalPartnerTab />}
        {v2On && tab === "corrective_leads" && <PmCorrectiveLeadsTab />}
        {v2On && tab === "water_health" && <PmWaterHealthTab />}
        {v2On && tab === "marketing"     && <PmPlaceholderTab testid="pm-tab-body-marketing"   title="Marketing"        subtitle="Agency-branded landlord flyer templates and shareable links." comingIn="Phase 8" />}
        {v2On && tab === "settings"      && <PmSettingsTab />}

        {(!v2On || tab === "properties") && (<>

        <div className="mt-10 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">
          <Building2 className="w-3.5 h-3.5" /> Portfolio
        </div>
        <div className="mt-2 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h2 data-testid="pm-dashboard-h1" className="font-display text-2xl lg:text-3xl font-bold text-slate-900">
              Managed Properties
            </h2>
            <p className="mt-2 text-slate-600 max-w-2xl text-sm sm:text-base">
              View AWARE Water Health Records, Water Health Scores, and retest
              dates for the properties assigned to your agency.
            </p>
          </div>
          <button
            type="button"
            data-testid="pm-add-property-btn"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold shadow-sm transition-colors self-start sm:self-auto whitespace-nowrap"
          >
            <Plus className="w-4 h-4" /> Add managed property
          </button>
        </div>

        {/* Phase 2L-A119 — KPI summary strip. Read-only chips wrapping
            a single client-side filter; clicking a card toggles the
            corresponding lens against the property grid. */}
        {!loading && !error && items.length > 0 && (
          <div data-testid="pm-kpi-strip" className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            <KpiCard
              testid="pm-kpi-total"
              label="Managed properties"
              value={kpis.total}
              icon={<Building2 className="w-4 h-4 text-cyan-700" />}
              active={!kpiFilter}
              onClick={() => setKpiFilter(null)}
              tone="default"
            />
            <KpiCard
              testid="pm-kpi-action"
              label="Critical results"
              value={kpis.action}
              icon={<AlertOctagon className="w-4 h-4 text-rose-700" />}
              active={kpiFilter === "action"}
              onClick={() => toggleKpiFilter("action")}
              tone="rose"
            />
            <KpiCard
              testid="pm-kpi-due-soon"
              label="Due soon"
              value={kpis.dueSoon}
              icon={<Clock className="w-4 h-4 text-amber-700" />}
              active={kpiFilter === "due_soon"}
              onClick={() => toggleKpiFilter("due_soon")}
              tone="amber"
            />
            <KpiCard
              testid="pm-kpi-overdue"
              label="Overdue"
              value={kpis.overdue}
              icon={<AlertTriangle className="w-4 h-4 text-rose-700" />}
              active={kpiFilter === "overdue"}
              onClick={() => toggleKpiFilter("overdue")}
              tone="rose"
            />
            <KpiCard
              testid="pm-kpi-awaiting"
              label="Awaiting first test"
              value={kpis.awaiting}
              icon={<FileQuestion className="w-4 h-4 text-slate-600" />}
              active={kpiFilter === "awaiting"}
              onClick={() => toggleKpiFilter("awaiting")}
              tone="slate"
            />
          </div>
        )}

        {/* Phase 2L-A132B — Action required list. Read-only summary of
            the most urgent items across the portfolio. Computed
            server-side in routers/property_manager.property_manager_properties
            from existing data — no extra round-trips, no schema changes,
            no chasing/SMS/email side-effects. */}
        {!loading && !error && actions.length > 0 && (
          <section
            data-testid="pm-action-required-section"
            className="mt-5 bg-white rounded-2xl border border-slate-200 p-5 sm:p-6"
          >
            <div className="flex items-center gap-2">
              <AlertOctagon className="w-4 h-4 text-rose-600" />
              <h2
                data-testid="pm-action-required-heading"
                className="font-display text-base sm:text-lg font-semibold text-slate-900"
              >
                Action required
              </h2>
              <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400">
                · {actions.length}
              </span>
            </div>
            <ul data-testid="pm-action-required-list" className="mt-3 divide-y divide-slate-100">
              {actions.slice(0, 12).map((a, idx) => {
                const tone = {
                  danger: { bg: "bg-rose-50", border: "border-rose-200", dot: "bg-rose-500", text: "text-rose-700" },
                  warn:   { bg: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-500", text: "text-amber-700" },
                  info:   { bg: "bg-cyan-50", border: "border-cyan-200", dot: "bg-cyan-500", text: "text-cyan-700" },
                }[a.severity] || { bg: "bg-slate-50", border: "border-slate-200", dot: "bg-slate-400", text: "text-slate-600" };
                const dollars = a.amount != null
                  ? new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(a.amount)
                  : null;
                return (
                  <li
                    key={`${a.type}-${a.property_id}-${idx}`}
                    data-testid={`pm-action-item-${a.type}-${a.property_id}`}
                    className="py-2.5 first:pt-0 last:pb-0 flex items-center gap-3"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${tone.dot} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-bold uppercase tracking-wider ${tone.text}`}>
                        {a.label}
                      </div>
                      <div className="text-sm text-slate-700 truncate">
                        {a.property_address || "—"}
                        {a.reference && (
                          <> · <span className="font-mono text-xs">{a.reference}</span></>
                        )}
                        {dollars && a.due_at && (
                          <> · {dollars} · due {formatDateAU(a.due_at)}</>
                        )}
                        {(!dollars && a.due_at && a.type === "overdue_retest") && (
                          <> · {formatDateAU(a.due_at)}</>
                        )}
                      </div>
                    </div>
                    {(a.type === "unpaid_invoice" || a.type === "overdue_invoice") && a.reference && (
                      <button
                        type="button"
                        onClick={() => downloadInvoice(a.reference)}
                        data-testid={`pm-action-download-invoice-${a.property_id}`}
                        className="text-xs text-cyan-700 hover:text-cyan-900 font-semibold inline-flex items-center gap-1 flex-shrink-0"
                      >
                        <Download className="w-3 h-3" /> Download invoice
                      </button>
                    )}
                    {a.link && (
                      <Link
                        to={a.link}
                        data-testid={`pm-action-link-${a.type}-${a.property_id}`}
                        className="text-xs text-cyan-700 hover:text-cyan-900 font-semibold inline-flex items-center gap-1 flex-shrink-0"
                      >
                        Open <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
            {actions.length > 12 && (
              <div className="mt-3 text-xs text-slate-500">
                Showing 12 of {actions.length} — review individual properties for the full list.
              </div>
            )}
          </section>
        )}

        {/* Active filter chip — clear via click */}
        {kpiFilter && (
          <button
            type="button"
            data-testid="pm-kpi-filter-chip"
            onClick={() => setKpiFilter(null)}
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-cyan-50 border border-cyan-200 px-3 py-1.5 text-xs font-semibold text-cyan-800 hover:bg-cyan-100"
          >
            <FilterIcon className="w-3 h-3" />
            Filter: {{ action: "Action required", due_soon: "Due soon", overdue: "Overdue", awaiting: "Awaiting first test" }[kpiFilter]}
            <span className="text-cyan-600">· clear ✕</span>
          </button>
        )}

        {loading && (
          <div className="mt-10 text-slate-500" data-testid="pm-loading">Loading portfolio…</div>
        )}

        {!loading && error && (
          <div data-testid="pm-error" className="mt-10 bg-white rounded-3xl border border-rose-200 p-8 text-rose-700">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div data-testid="pm-empty" className="mt-10 bg-white rounded-3xl border border-slate-200 p-10 text-center">
            <Building2 className="w-10 h-10 text-cyan-500 mx-auto mb-3" />
            <h3 className="font-display font-semibold text-slate-900 text-lg">No managed properties yet</h3>
            <p className="text-slate-500 mt-2 max-w-md mx-auto">
              Once Aware assigns properties to your agency, they will appear here.
            </p>
            <button
              type="button"
              data-testid="pm-empty-add-property-btn"
              onClick={() => setShowAdd(true)}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold"
            >
              <Plus className="w-4 h-4" /> Add your first property
            </button>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <div data-testid="pm-property-grid" className="mt-8 grid lg:grid-cols-2 gap-5">
            {filteredItems.map(({ property, latest_test, next_retest, has_pending_booking, pending_booking }) => {
              const due = next_retest?.next_due_at;
              const overdue = due && new Date(due) < now;
              const hasTests = !!latest_test;
              const hasPendingBooking = !!has_pending_booking;
              const bookHref = `/book?property=${encodeURIComponent(property.id)}`
                + (property.suburb ? `&suburb=${encodeURIComponent(property.suburb)}` : "")
                + (property.postcode ? `&postcode=${encodeURIComponent(property.postcode)}` : "")
                + (property.sources?.[0]?.type === "bore" ? "&test=bore" : "&test=tank");
              return (
                <div
                  key={property.id}
                  data-testid={`pm-property-card-${property.id}`}
                  className="group bg-white rounded-3xl border border-slate-200 p-7 hover:shadow-xl hover:border-cyan-200 hover:-translate-y-0.5 transition-all"
                >
                  <Link
                    to={`/passport/property/${property.id}`}
                    className="block"
                    data-testid={`pm-property-passport-link-${property.id}`}
                  >
                    <div className="flex items-start justify-between gap-5">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs text-cyan-600 font-semibold uppercase tracking-wider">
                          <MapPin className="w-3.5 h-3.5" />
                          {property.property_type || "property"}
                        </div>
                        <div className="mt-2 font-display font-bold text-slate-900 text-lg leading-snug">
                          {property.address}
                        </div>
                        {property.owner_name && (
                          <div className="mt-1 text-xs text-slate-500">
                            Owner: {property.owner_name}
                          </div>
                        )}
                      </div>
                      {hasTests ? (
                        <HealthScoreGauge
                          score={latest_test.health_score}
                          band={latest_test.band}
                          size={120}
                        />
                      ) : hasPendingBooking ? (
                        <span
                          data-testid={`pm-property-pending-booking-pill-${property.id}`}
                          className="text-[10px] uppercase tracking-wider font-bold text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2.5 py-1 whitespace-nowrap"
                        >
                          First test booked
                        </span>
                      ) : (
                        <span
                          data-testid={`pm-property-no-tests-pill-${property.id}`}
                          className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 whitespace-nowrap"
                        >
                          First test not booked
                        </span>
                      )}
                    </div>
                  </Link>

                  {hasTests ? (
                    <>
                      <div className="mt-6 grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Last tested</div>
                          <div className="mt-0.5 text-slate-700 font-medium">
                            {latest_test?.collected_at ? formatDateAU(latest_test.collected_at) : "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Next retest due</div>
                          <div className={`mt-0.5 font-medium inline-flex items-center gap-1 ${
                            overdue ? "text-rose-700" : "text-slate-700"
                          }`}>
                            {overdue && <AlertTriangle className="w-3 h-3" />}
                            <Calendar className="w-3 h-3" />
                            {due ? formatDateAU(due) : "—"}
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 flex items-center justify-between text-xs">
                        <Link
                          to={`/passport/property/${property.id}`}
                          className="text-cyan-700 font-semibold inline-flex items-center gap-1 hover:text-cyan-900"
                        >
                          <ShieldCheck className="w-3.5 h-3.5" /> View Water Record
                          <ArrowRight className="w-3 h-3" />
                        </Link>
                        {latest_test?.report_pdf_url && (
                          <a
                            href={latest_test.report_pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`pm-coa-link-${property.id}`}
                            className="text-slate-500 hover:text-cyan-700 inline-flex items-center gap-1"
                          >
                            <FileText className="w-3 h-3" /> Latest COA
                          </a>
                        )}
                      </div>
                    </>
                  ) : hasPendingBooking ? (
                    <>
                      <div
                        data-testid={`pm-property-pending-booking-${property.id}`}
                        className="mt-5 rounded-2xl bg-cyan-50 border border-cyan-200 p-4"
                      >
                        <div className="text-sm font-semibold text-cyan-900 inline-flex items-center gap-1.5">
                          <ShieldCheck className="w-4 h-4" /> Booked · awaiting sample
                        </div>
                        <div className="mt-1 text-xs text-cyan-800/90 leading-relaxed">
                          {pending_booking?.test_name || "AWARE Water Health Record test"}
                          {pending_booking?.reference && (
                            <> · Ref <span className="font-mono">{pending_booking.reference}</span></>
                          )}
                          {pending_booking?.purchase_order_number && (
                            <> · PO {pending_booking.purchase_order_number}</>
                          )}
                        </div>
                        {pending_booking?.payment_terms === "30_days" && pending_booking?.payment_due_at && (
                          <div className="mt-1 text-[11px] text-cyan-700/90">
                            Net-30 invoice · due {formatDateAU(pending_booking.payment_due_at)}
                          </div>
                        )}
                      </div>
                      {pending_booking?.invoice_required && !pending_booking?.is_paid && (() => {
                        const due = pending_booking?.payment_due_at
                          ? new Date(pending_booking.payment_due_at) : null;
                        const isOverdue = !!(due && due < now);
                        const amt = pending_booking?.final_total
                          ?? pending_booking?.total ?? pending_booking?.subtotal;
                        const dollars = amt != null
                          ? new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(amt)
                          : null;
                        const sty = isOverdue
                          ? "bg-rose-50 border-rose-200 text-rose-700"
                          : "bg-amber-50 border-amber-200 text-amber-700";
                        return (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <div
                              data-testid={`pm-property-invoice-chip-${property.id}`}
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${sty}`}
                            >
                              <AlertTriangle className="w-3 h-3" />
                              {isOverdue ? "Overdue invoice" : "Outstanding invoice"}
                              {dollars && <> · {dollars}</>}
                              {due && <> · due {formatDateAU(pending_booking.payment_due_at)}</>}
                            </div>
                            {pending_booking?.reference && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault(); e.stopPropagation();
                                  downloadInvoice(pending_booking.reference);
                                }}
                                data-testid={`pm-property-download-invoice-${property.id}`}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-cyan-700 hover:text-cyan-900"
                              >
                                <Download className="w-3 h-3" /> Download invoice
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      <div className="mt-4 flex items-center justify-between">
                        <Link
                          to={`/passport/property/${property.id}`}
                          data-testid={`pm-property-view-booked-${property.id}`}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white border border-cyan-200 text-cyan-700 hover:bg-cyan-50 text-xs font-semibold"
                        >
                          <ShieldCheck className="w-3.5 h-3.5" /> View details
                          <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        data-testid={`pm-property-empty-state-${property.id}`}
                        className="mt-5 rounded-2xl bg-slate-50 border border-dashed border-slate-200 p-4"
                      >
                        <div className="text-sm font-semibold text-slate-900">
                          No water test history yet
                        </div>
                        <div className="mt-0.5 text-xs text-slate-500">
                          First test not booked — book a Water Health
                          AWARE Water Health Record test to start.
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-between">
                        <Link
                          to={bookHref}
                          data-testid={`pm-property-book-first-${property.id}`}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold"
                        >
                          <TestTube2 className="w-3.5 h-3.5" /> Book first test
                          <ArrowRight className="w-3 h-3" />
                        </Link>
                        <Link
                          to={`/passport/property/${property.id}`}
                          className="text-xs text-slate-500 hover:text-cyan-700 inline-flex items-center gap-1"
                        >
                          View details <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && !error && items.length > 0 && kpiFilter && filteredItems.length === 0 && (
          <div
            data-testid="pm-kpi-empty-filter"
            className="mt-6 bg-white rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500"
          >
            No properties match this filter. <button onClick={() => setKpiFilter(null)} className="text-cyan-700 font-semibold hover:underline">Clear filter</button>
          </div>
        )}
        </>)}
      </main>

      <PmAddPropertyModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={() => { setLoading(true); loadPortfolio(); }}
        onCreatedAndBook={(property) => {
          if (property?.id) {
            // Phase 2L-A121.B — create-first, then book hand-off.
            navigate(`/book?property=${encodeURIComponent(property.id)}`
              + (property.sources?.[0]?.type === "bore" ? "&test=bore" : "&test=tank"));
          } else {
            setLoading(true);
            loadPortfolio();
          }
        }}
      />
    </div>
  );
}

// Phase 2L-A119 — compact KPI card used by the dashboard summary strip.
// Tone maps to a soft accent band; `active` lifts the active filter
// visually without changing component structure.
function KpiCard({ testid, label, value, icon, active, onClick, tone }) {
  const toneRing = {
    rose:    "ring-rose-300 bg-rose-50",
    amber:   "ring-amber-300 bg-amber-50",
    slate:   "ring-slate-300 bg-slate-50",
    default: "ring-cyan-300 bg-cyan-50",
  }[tone] || "ring-cyan-300 bg-cyan-50";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      aria-pressed={active}
      className={
        "group text-left rounded-2xl border bg-white px-4 py-3 transition-all hover:-translate-y-0.5 hover:shadow " +
        (active
          ? `border-transparent ring-2 ${toneRing}`
          : "border-slate-200 hover:border-cyan-200")
      }
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 truncate">{label}</span>
      </div>
      <div className="mt-1.5 font-display text-2xl sm:text-3xl font-bold text-slate-900 tabular-nums">{value}</div>
    </button>
  );
}
